import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Search, Menu, X, LogOut } from "lucide-react";

const LOGO_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_wellness-hub-2272/artifacts/opuacvgq_amiroop.PNG.png";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    nav(`/products${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setMobile(false);
  };

  const roleDashboard =
    user && user.role === "admin"
      ? "/admin"
      : user && user.role === "vendor"
      ? "/vendor"
      : user && user.role === "practitioner"
      ? "/practitioner/dashboard"
      : "/account";

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="bg-[#1E3A2B] text-[#FAF8F5] text-[11px] font-mono">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-8 flex items-center justify-between gap-4">
          <div className="hidden sm:flex items-center gap-4 opacity-90">
            <a href="tel:+917080800384" className="hover:text-[#D97706] flex items-center gap-1" data-testid="topbar-phone">📞 +91 70808 00384</a>
            <a href="mailto:care@healthioindia.com" className="hover:text-[#D97706] flex items-center gap-1" data-testid="topbar-email">✉ care@healthioindia.com</a>
          </div>
          <div className="opacity-80 tracking-wider uppercase text-[10px]">Amiroop Healthion LLP · www.healthionindia.com</div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img
              src={LOGO_URL}
              alt="Amiroop Healthion"
              className="w-14 h-14 object-contain"
              style={{ mixBlendMode: "multiply" }}
            />
            <div className="leading-tight">
              <div className="font-serif text-xl font-bold text-[#1E3A2B]">Healthion India</div>
              <div className="text-[10px] font-mono tracking-wider text-[#5A6B5D] uppercase">A Curated Ayurveda Marketplace</div>
            </div>
          </Link>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl">
            <div className="w-full flex items-center border border-[#E2DDD5] rounded-full bg-white pl-4 pr-1 py-1">
              <Search className="w-4 h-4 text-[#5A6B5D]" />
              <input
                data-testid="global-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search Ayurvedic products, doshas, benefits..."
                className="flex-1 bg-transparent px-3 py-1.5 outline-none text-sm"
              />
              <button type="submit" className="bg-[#1E3A2B] text-white text-xs font-semibold px-4 py-2 rounded-full" data-testid="search-submit-button">
                Search
              </button>
            </div>
          </form>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/vendor/register" className="text-sm font-semibold text-[#1E3A2B] hover:text-[#D97706]" data-testid="sell-on-healthion-link">
              Sell on Healthion
            </Link>
            <button onClick={() => setOpen(true)} className="relative p-2" data-testid="open-cart-button">
              <ShoppingBag className="w-5 h-5 text-[#1A201C]" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#D97706] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            {user && user !== false ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 rounded-full bg-[#E8F0EA] text-[#1E3A2B]" data-testid="user-menu-button">
                    <User className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-[#E2DDD5] w-56">
                  <DropdownMenuLabel className="text-xs">
                    <div className="font-semibold text-[#1A201C]">{user.name}</div>
                    <div className="text-[#5A6B5D] font-mono">{user.role.toUpperCase()}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={roleDashboard} data-testid="menu-dashboard-link">Dashboard</Link>
                  </DropdownMenuItem>
                  {user.role === "customer" && (
                    <DropdownMenuItem asChild>
                      <Link to="/account" data-testid="menu-orders-link">My Orders</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout-button">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="btn-forest text-sm py-2 px-5" data-testid="header-login-button">
                Sign In
              </Link>
            )}
          </div>

          <button className="md:hidden p-2" onClick={() => setMobile((m) => !m)} data-testid="mobile-menu-toggle">
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        <nav className="hidden md:flex items-center gap-6 pb-3 text-sm text-[#1A201C]">
          <Link to="/products" className="hover:text-[#D97706]" data-testid="nav-all-products">All Products</Link>
          <Link to="/products?category=herbal-supplements" className="hover:text-[#D97706]">Herbal Supplements</Link>
          <Link to="/products?category=personal-care" className="hover:text-[#D97706]">Personal Care</Link>
          <Link to="/products?category=immunity-boosters" className="hover:text-[#D97706]">Immunity</Link>
          <Link to="/products?category=organic-teas" className="hover:text-[#D97706]">Organic Teas</Link>
          <Link to="/products?category=essential-oils" className="hover:text-[#D97706]">Oils & Serums</Link>
          <Link to="/products?category=indian-masala" className="hover:text-[#D97706]" data-testid="nav-indian-masala">Indian Masala</Link>
          <Link to="/blog" className="hover:text-[#D97706]" data-testid="nav-blog">Journal</Link>
          <Link to="/practitioners" className="hover:text-[#D97706]" data-testid="nav-practitioners">Consult a Doctor</Link>
          <Link to="/faq" className="hover:text-[#D97706]" data-testid="nav-faq">FAQ</Link>
          <span className="ml-auto text-xs font-mono text-[#5A6B5D]">A CURATED AYURVEDA MARKETPLACE</span>
        </nav>

        {mobile && (
          <div className="md:hidden pb-4 space-y-3">
            <form onSubmit={submitSearch} className="flex items-center border border-[#E2DDD5] rounded-full bg-white pl-4 pr-1 py-1">
              <Search className="w-4 h-4 text-[#5A6B5D]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="flex-1 bg-transparent px-3 py-1.5 outline-none text-sm" />
              <button className="bg-[#1E3A2B] text-white text-xs font-semibold px-4 py-2 rounded-full">Go</button>
            </form>
            <Link to="/products" className="block py-2 text-sm">All Products</Link>
            <Link to="/vendor/register" className="block py-2 text-sm font-semibold text-[#1E3A2B]">Sell on Healthion</Link>
            <button onClick={() => { setOpen(true); setMobile(false); }} className="block py-2 text-sm">Cart ({count})</button>
            {user && user !== false ? (
              <>
                <Link to={roleDashboard} className="block py-2 text-sm">Dashboard</Link>
                <button onClick={logout} className="block py-2 text-sm">Logout</button>
              </>
            ) : (
              <Link to="/login" className="block btn-forest text-center text-sm">Sign In</Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
